import { Grid, Switch, FormControlLabel } from '@mui/material';
import { useCallback, useState } from 'react';
import { ICandidate, ICandidateRevenue, amplitude } from 'wisestep-api';

import {
  getPersonalDetails,
  getProfessionalDetails,
  getCompenstation,
  getJobPreferencInfo,
  getOtherInfo,
} from '../../../utils/CandidateHelper';
import CandidateDetailsCard from '../CandidateDetailsCard';
import CandidateCertificateCard from '../CandidateDetailsCard/CandidateCertificateCard';
import { CandidateDetails, SplicedData } from '../CandidateDetailsCard/CandidateDetailsCard';
import CandidateEducationCard from '../CandidateDetailsCard/CandidateEducationCard';
import CandidateExperienceCard from '../CandidateDetailsCard/CandidateExperienceCard';
interface CandidateProfileDetailsProps {
  candidate: ICandidate;
  revenue?: ICandidateRevenue;
}

interface Details {
  personal: SplicedData;
  professional: SplicedData;
  compensation: SplicedData;
  jobPreferences: SplicedData;
  otherInfo: SplicedData;
}

const CandidateProfileDetails = ({ candidate, revenue }: CandidateProfileDetailsProps) => {
  const [showMissingInfo, setShowMissingInfo] = useState(false);

  const onShowMissingInfo = (details: CandidateDetails[], showDetails: boolean) => {
    const updatedDetails = [
      ...details.filter(
        (detail) =>
          showDetails ||
          typeof detail.value !== 'string' ||
          (typeof detail.value === 'string' && detail.value !== 'NA')
      ),
    ];
    return updatedDetails;
  };
  const spliceData = (candidateDetails: CandidateDetails[]) => {
    const fullWidth = [...candidateDetails];
    const medium = [...candidateDetails];
    const defaultData = [...candidateDetails];
    return {
      5: {
        initial: fullWidth.slice(0, 5),
        hidden: fullWidth.slice(5, fullWidth.length),
      },
      4: {
        initial: medium.slice(0, 4),
        hidden: medium.slice(4, medium.length),
      },
      3: {
        initial: defaultData.slice(0, 3),
        hidden: defaultData.slice(3, defaultData.length),
      },
      2: {
        initial: defaultData.slice(0, 2),
        hidden: defaultData.slice(2, defaultData.length),
      },
    };
  };
  const setDisplayDetailsData = useCallback(
    (displayMissingInfo: boolean) => {
      return {
        personal: spliceData(onShowMissingInfo(getPersonalDetails(candidate), displayMissingInfo)),
        professional: spliceData(
          onShowMissingInfo(getProfessionalDetails(candidate), displayMissingInfo)
        ),
        compensation: spliceData(
          onShowMissingInfo(getCompenstation(candidate, revenue), displayMissingInfo)
        ),
        jobPreferences: spliceData(
          onShowMissingInfo(getJobPreferencInfo(candidate), displayMissingInfo)
        ),
        otherInfo: spliceData(onShowMissingInfo(getOtherInfo(candidate), displayMissingInfo)),
      };
    },
    [candidate, revenue]
  );

  const [displayDetails, setDetails] = useState<Details>(setDisplayDetailsData(false));

  const toggleInfo = () => {
    setDetails(setDisplayDetailsData(!showMissingInfo));
    setShowMissingInfo(!showMissingInfo);
  };
  return (
    <Grid container rowSpacing={2}>
      <Grid item xs={12}>
        <CandidateDetailsCard
          title={'Personal and Contact Information'}
          details={displayDetails.personal}
          isProfile={candidate.isJobCandidateMapping ?? true}
          candidate={candidate}
        />
      </Grid>

      <Grid item xs={12}>
        <CandidateDetailsCard
          title={'Professional Background and Experience'}
          details={displayDetails.professional}
          isProfile={candidate.isJobCandidateMapping ?? true}
          candidate={candidate}
        />
      </Grid>
      <Grid item xs={12}>
        <CandidateDetailsCard
          title={'Compensation and Benefits'}
          details={displayDetails.compensation}
          isProfile={candidate.isJobCandidateMapping ?? true}
          candidate={candidate}
        />
      </Grid>
      <Grid item xs={12}>
        <CandidateDetailsCard
          title={'Skills'}
          skillDetails={candidate.skills}
          isSkills={true}
          isProfile={candidate.isJobCandidateMapping ?? true}
          candidate={candidate}
        />
      </Grid>
      <Grid item xs={12}>
        <CandidateDetailsCard
          title={'Job Preferences and Availability'}
          details={displayDetails.jobPreferences}
          isProfile={candidate.isJobCandidateMapping ?? true}
          candidate={candidate}
        />
      </Grid>

      <Grid item xs={12}>
        <CandidateDetailsCard
          title={'Other Information'}
          details={displayDetails.otherInfo}
          showMissingInfo={showMissingInfo}
          isProfile={candidate.isJobCandidateMapping ?? true}
          candidate={candidate}
        />
      </Grid>
      <Grid item xs={12}>
        <CandidateExperienceCard title={'Experience'} candidate={candidate} />
      </Grid>
      <Grid item xs={12}>
        <CandidateEducationCard title={'Education'} candidate={candidate} />
      </Grid>
      <Grid item xs={12}>
        <CandidateCertificateCard title={'Certifications'} candidate={candidate} />
      </Grid>
      <Grid item pl={1.5}>
        <FormControlLabel
          sx={{
            columnGap: 1,
          }}
          control={
            <Switch
              value={showMissingInfo}
              onChange={() => {
                toggleInfo();
                amplitude('CandidateView_ShowMissingInfo', {
                  clickedOn: 'ShowMissingInfo',
                  pageName: 'candidate-layout',
                  pageUrl: window.location.href,
                });
              }}
            />
          }
          label="Show missing info"
          labelPlacement="end"
        />
      </Grid>
    </Grid>
  );
};

export default CandidateProfileDetails;
